'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user previously dismissed
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      // Show banner after a short delay so it doesn't appear immediately on load
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
      setIsInstalled(true);
    }
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showBanner || isInstalled || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[360px] z-50 rounded-2xl shadow-2xl border p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4"
      style={{
        background: 'var(--ink)',
        borderColor: 'rgba(255,255,255,0.12)',
      }}
    >
      {/* App icon */}
      <div
        className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
        style={{ background: 'var(--brand)' }}
      >
        <Icon name="BuildingLibraryIcon" size={24} className="text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white leading-tight">Install WENLSU Alumni SACCO</p>
        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
          Add to your desktop for quick access — works offline too.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleInstall}
            className="flex-1 text-xs font-semibold py-2 px-3 rounded-lg transition-all hover:opacity-90 active:scale-95"
            style={{ background: 'var(--brand-link)', color: '#fff' }}
          >
            Install App
          </button>
          <button
            onClick={handleDismiss}
            className="text-xs font-medium py-2 px-3 rounded-lg transition-all hover:opacity-80"
            style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.08)' }}
          >
            Not now
          </button>
        </div>
      </div>

      {/* Close */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-colors hover:bg-white/10"
        style={{ color: 'rgba(255,255,255,0.5)' }}
        aria-label="Dismiss install prompt"
      >
        <Icon name="XMarkIcon" size={14} />
      </button>
    </div>
  );
}
